import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Group, SegmentedControl, Select, Stack, Text, Tooltip } from '@mantine/core';
import { BookOpenCheck, ClipboardCheck, Download, FileBarChart } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { Button, Card, EmptyState, PageContainer, PageHeader, Pill, Section } from '../components/ui';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { downloadCohortCsv, useCohortMatrix, useGradebookCohorts } from '../hooks/useGradebook';
import type { GradebookCell } from '../api/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/admin/gradebook/')({
  component: Gradebook,
  validateSearch: (s: Record<string, unknown>) => ({
    cohort: typeof s.cohort === 'string' ? s.cohort : undefined,
  }),
});

/**
 * Gradebook — the cohort × lab matrix. An instructor sees the cohorts they
 * own; an org admin sees all. Click a cell to open the student's attempts.
 */
function Gradebook() {
  const search = Route.useSearch() as { cohort?: string };
  const navigate = useNavigate();
  const cohorts = useGradebookCohorts();
  const [cohortId, setCohortId] = useState<string | undefined>(search.cohort);
  const [scope, setScope] = useState<'all' | 'assigned'>('all');

  // Default to the first cohort so the page never opens empty for an
  // instructor with exactly one class.
  useEffect(() => {
    if (!cohortId && cohorts.data && cohorts.data.length > 0) {
      setCohortId(String(cohorts.data[0]!.id));
    }
  }, [cohortId, cohorts.data]);

  const matrix = useCohortMatrix(cohortId, scope === 'assigned');
  const cohortName = cohorts.data?.find((c) => String(c.id) === cohortId)?.name ?? 'cohort';

  const options = useMemo(
    () => (cohorts.data ?? []).map((c) => ({ value: String(c.id), label: `${c.name} (${c.traineeCount})` })),
    [cohorts.data],
  );

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={BookOpenCheck}
        title="Gradebook"
        description="Every student, every lab, every attempt — for the cohorts you teach."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Gradebook' }]}
        actions={
          <Group gap="xs">
            <Link to="/admin/gradebook/$cohortId/report" params={{ cohortId: cohortId ?? '' }} style={{ textDecoration: 'none' }} disabled={!cohortId}>
              <Button variant="ghost" leftIcon={<FileBarChart size={14} />} disabled={!cohortId}>
                Report
              </Button>
            </Link>
            <Link to="/admin/review" style={{ textDecoration: 'none' }}>
              <Button variant="ghost" leftIcon={<ClipboardCheck size={14} />}>
                Review queue
                {matrix.data && matrix.data.summary.pending_review > 0 ? (
                  <Pill variant="warn" size="xs" style={{ marginLeft: 6 }}>
                    {matrix.data.summary.pending_review}
                  </Pill>
                ) : null}
              </Button>
            </Link>
            <Button
              variant="secondary"
              leftIcon={<Download size={14} />}
              disabled={!cohortId || !matrix.data}
              onClick={() => cohortId && void downloadCohortCsv(cohortId, cohortName)}
            >
              Export CSV
            </Button>
          </Group>
        }
      />

      <Section
        title="Cohort"
        description="Scores are the lab's percentage; a dot marks answers waiting for your review."
        action={
          <Select
            data={options}
            value={cohortId ?? null}
            onChange={(v) => {
              setCohortId(v ?? undefined);
              void navigate({ to: '/admin/gradebook', search: { cohort: v ?? undefined } });
            }}
            placeholder={cohorts.isPending ? 'Loading cohorts…' : 'Choose a cohort'}
            style={{ minWidth: 280 }}
            aria-label="Cohort"
            searchable
          />
        }
      >
        {cohorts.isError ? (
          <ErrorState
            title="Could not load cohorts"
            message={(cohorts.error as Error)?.message ?? 'Unknown error'}
            onRetry={() => void cohorts.refetch()}
          />
        ) : cohorts.isPending ? (
          <LoadingState rows={3} height={32} />
        ) : options.length === 0 ? (
          <EmptyState
            title="No cohorts yet"
            description="Create a cohort and add students to it; their lab results will appear here."
            cta={{ label: 'Go to cohorts', onClick: () => void navigate({ to: '/admin/cohorts' }) }}
          />
        ) : matrix.isError ? (
          <ErrorState
            title="Could not load the gradebook"
            message={(matrix.error as Error)?.message ?? 'Unknown error'}
            onRetry={() => void matrix.refetch()}
          />
        ) : matrix.isPending || !matrix.data ? (
          <LoadingState rows={6} height={28} />
        ) : (
          <Stack gap="md">
            <Group justify="space-between" wrap="wrap">
              <SummaryRow m={matrix.data.summary} />
              <SegmentedControl
                size="xs"
                value={scope}
                onChange={(v) => setScope(v as 'all' | 'assigned')}
                data={[{ label: 'All labs', value: 'all' }, { label: 'Assigned only', value: 'assigned' }]}
              />
            </Group>
            {matrix.data.students.length === 0 ? (
              <EmptyState
                title="No students in this cohort"
                description="Add students from the cohort page; they will show up here as soon as they start a lab."
              />
            ) : (
              <Card>
                <div style={{ overflowX: 'auto' }}>
                  <table className="forge-gradebook" style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th style={thStyle({ sticky: true })}>Student</th>
                        {matrix.data.labs.map((l) => (
                          <th key={l.slug} style={thStyle({})} title={l.name}>
                            <Text size="xs" fw={600} style={{ whiteSpace: 'nowrap' }}>
                              {l.slug}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {l.points_possible} pts
                            </Text>
                          </th>
                        ))}
                        <th style={thStyle({})}>Avg</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrix.data.students.map((s) => {
                        const scored: number[] = [];
                        return (
                          <tr key={s.user_id}>
                            <td style={tdStyle({ sticky: true })}>
                              <Link
                                to="/admin/gradebook/$cohortId/$userId"
                                params={{ cohortId: cohortId ?? '', userId: String(s.user_id) }}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                              >
                                <Text size="sm" fw={500}>
                                  {s.username}
                                </Text>
                              </Link>
                            </td>
                            {matrix.data!.labs.map((l) => {
                              const cell = matrix.data!.cells[`${s.user_id}:${l.slug}`];
                              if (cell?.score_pct != null) scored.push(cell.score_pct);
                              return (
                                <td key={l.slug} style={tdStyle({ center: true })}>
                                  <Link
                                    to="/admin/gradebook/$cohortId/$userId"
                                    params={{ cohortId: cohortId ?? '', userId: String(s.user_id) }}
                                    style={{ textDecoration: 'none' }}
                                    aria-label={`${s.username} — ${l.name}`}
                                  >
                                    <ScoreChip cell={cell} />
                                  </Link>
                                </td>
                              );
                            })}
                            <td style={tdStyle({ center: true })}>
                              <Text size="sm" fw={600} ff="JetBrains Mono">
                                {scored.length ? `${(scored.reduce((a, b) => a + b, 0) / scored.length).toFixed(0)}%` : '—'}
                              </Text>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </Stack>
        )}
      </Section>
    </PageContainer>
  );
}

function SummaryRow({ m }: { m: { students: number; labs: number; completed: number; in_progress: number; pending_review: number; avg_score_pct: number | null } }) {
  const items: Array<[string, string, 'neutral' | 'info' | 'success' | 'warn']> = [
    ['Students', String(m.students), 'neutral'],
    ['Labs', String(m.labs), 'neutral'],
    ['Completed', String(m.completed), 'success'],
    ['In progress', String(m.in_progress), 'info'],
    ['Awaiting review', String(m.pending_review), m.pending_review > 0 ? 'warn' : 'neutral'],
    ['Average', m.avg_score_pct == null ? '—' : `${m.avg_score_pct.toFixed(0)}%`, 'neutral'],
  ];
  return (
    <Group gap="lg" wrap="wrap">
      {items.map(([label, value, variant]) => (
        <Group key={label} gap={6}>
          <Text size="xs" c="dimmed">
            {label}
          </Text>
          <Pill variant={variant} size="sm">
            {value}
          </Pill>
        </Group>
      ))}
    </Group>
  );
}

function ScoreChip({ cell }: { cell: GradebookCell | undefined }) {
  if (!cell) {
    return (
      <Text size="xs" c="dimmed" aria-label="not started">
        ·
      </Text>
    );
  }
  const pct = cell.score_pct;
  const variant: 'neutral' | 'danger' | 'warn' | 'success' | 'info' =
    pct == null ? 'info' : pct < 50 ? 'danger' : pct < 80 ? 'warn' : 'success';
  const label = pct == null ? cell.status : `${pct.toFixed(0)}%`;
  return (
    <Tooltip
      label={`${cell.status} · ${cell.attempts_used} attempt${cell.attempts_used === 1 ? '' : 's'}${cell.pending_review ? ` · ${cell.pending_review} awaiting review` : ''}`}
      withArrow
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Pill variant={variant} size="xs">
          {label}
        </Pill>
        {cell.pending_review > 0 ? (
          <span
            aria-label="awaiting review"
            style={{ width: 6, height: 6, borderRadius: 3, background: 'rgb(var(--c-amber))', display: 'inline-block' }}
          />
        ) : null}
      </span>
    </Tooltip>
  );
}

function thStyle(o: { sticky?: boolean }): React.CSSProperties {
  return {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '1px solid rgb(var(--c-bd))',
    position: o.sticky ? 'sticky' : undefined,
    left: o.sticky ? 0 : undefined,
    background: 'rgb(var(--c-card))',
    zIndex: o.sticky ? 1 : undefined,
  };
}

function tdStyle(o: { sticky?: boolean; center?: boolean }): React.CSSProperties {
  return {
    padding: '8px 12px',
    borderBottom: '1px solid rgb(var(--c-bd-subtle))',
    textAlign: o.center ? 'center' : 'left',
    position: o.sticky ? 'sticky' : undefined,
    left: o.sticky ? 0 : undefined,
    background: 'rgb(var(--c-card))',
    whiteSpace: 'nowrap',
  };
}
